import { lazy, Suspense, type ReactNode } from 'react';
import { useLocation } from 'wouter';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { LoadingFallback } from '@/components/LoadingFallback';
import { useAuth } from '@/contexts/auth-context';
import { ownerAreaRoles } from '@/features/quotes/owner-area-path';

const AdminLayout = lazy(() => import('@/layouts/admin-layout').then((m) => ({ default: m.AdminLayout })));
const MainLayout = lazy(() => import('@/layouts/main-layout').then((m) => ({ default: m.MainLayout })));

// Suspense boundary for lazily loaded pages: the shell (sidebar, top bar) stays on screen while a page chunk downloads.
function Page({ children }: { children: ReactNode }) {
  return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;
}

/**
 * Quote-first owner screens: admins and staff (decision 6), inside the admin layout. Estimators get in for their own
 * quotes and clients only (ESTIMATOR_PATHS); the server is the guard.
 */
export function OwnerRoute({ children }: { children: ReactNode }) {
  const [pathname] = useLocation();
  return (
    <ProtectedRoute allow={ownerAreaRoles(pathname)}>
      <AdminLayout>
        <Page>{children}</Page>
      </AdminLayout>
    </ProtectedRoute>
  );
}

/** The profile tab of Settings: everyone who works in the admin layout, estimators included (their sidebar profile link lands here). */
export function StaffSettingsRoute({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute allow={['admin', 'staff', 'estimator']}>
      <AdminLayout>
        <Page>{children}</Page>
      </AdminLayout>
    </ProtectedRoute>
  );
}

// Layout wrapper that uses AdminLayout for admin, staff and estimators (sidebar navigation), MainLayout for customers
export function ProfileLayoutWrapper({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role === 'admin' || user?.role === 'staff' || user?.role === 'estimator') {
    return <AdminLayout>{children}</AdminLayout>;
  }
  return <MainLayout>{children}</MainLayout>;
}
